import { NON_PRESERVED_QUERY_PARAMS } from '@/constants/nonPreservedQueryParams';

/**
 * Splits a `to` string (as accepted by React Router's navigate/Link/Navigate)
 * into path, search and hash parts WITHOUT promoting relative paths to
 * absolute ones.
 *
 * `new URL('workflows', 'http://x')` would turn the route-relative target
 * `"workflows"` into `/workflows` (absolute root path), breaking React
 * Router's nested-route resolution. This helper preserves the path string
 * exactly as the caller wrote it.
 *
 * @param to - A target string, e.g. `"workflows"`, `"/settings?tab=general"`, `"?env=staging"`.
 * @returns An object with the path, search (including leading `?` if present)
 *          and hash (including leading `#` if present) parts.
 */
export function splitTo(to: string): { path: string; search: string; hash: string } {
  let rest = to;
  let hash = '';
  const hashIdx = rest.indexOf('#');
  if (hashIdx >= 0) {
    hash = rest.slice(hashIdx);
    rest = rest.slice(0, hashIdx);
  }
  let search = '';
  const qIdx = rest.indexOf('?');
  if (qIdx >= 0) {
    search = rest.slice(qIdx);
    rest = rest.slice(0, qIdx);
  }
  return { path: rest, search, hash };
}

/**
 * Merges two URL query strings using target-wins semantics.
 *
 * If both `currentSearch` and `targetSearch` define the same key, the value
 * from `targetSearch` wins. This is used by the preserved navigation
 * primitives (`usePreservedNavigate`, `<PreservedLink>`, `<PreservedNavigate>`)
 * to retain environment params like `?env=staging` across SPA navigations
 * while still allowing a caller to override specific keys via the target URL.
 *
 * @remarks
 * Keys in `NON_PRESERVED_QUERY_PARAMS` (one-shot action params such as
 * `applyTemplate`) are ALWAYS removed from `currentSearch` before merging,
 * regardless of `dropParams`. A caller can still deliver one via `targetSearch`
 * (target wins), but it is never carried over from the current URL — see
 * issue #2688.
 *
 * @param currentSearch - The current URL's search string (e.g. `?env=staging`).
 * @param targetSearch  - The target URL's search string (e.g. `?tab=general`).
 * @param dropParams    - Optional list of keys to remove from `currentSearch`
 *                        before merging (escape hatch for callers that want
 *                        to explicitly drop a preserved param, e.g. on logout).
 * @returns The merged search string with a leading `?`, or `''` if empty.
 */
export function mergeSearch(
  currentSearch: string,
  targetSearch: string,
  dropParams?: string[],
): string {
  const current = new URLSearchParams(currentSearch);
  const target = new URLSearchParams(targetSearch);

  // One-shot action params (e.g. `applyTemplate`) are never carried over from
  // the current URL — see NON_PRESERVED_QUERY_PARAMS. A caller can still set
  // one explicitly via `targetSearch`, which wins below.
  for (const key of NON_PRESERVED_QUERY_PARAMS) {
    current.delete(key);
  }

  if (dropParams) {
    for (const key of dropParams) {
      current.delete(key);
    }
  }

  const merged = new URLSearchParams(current);
  for (const [key, value] of target) {
    merged.set(key, value);
  }

  const result = merged.toString();
  return result ? `?${result}` : '';
}
